/**
 * When does a planet's tile cycle complete?
 *
 * One source of truth, because the client and the server each used to answer
 * this separately and disagreed by a whole turn: the card showed "about to
 * upgrade!" on a turn the engine would not even attempt the upgrade, so the
 * player watched nothing happen and got no explanation, because the engine
 * never tried and therefore never reported a shortfall.
 *
 * `sub_levels_current` accrues one per turn and `tile` is fractional in
 * token-disabled games (2.5, 2.3, ... 1.5), so this is a threshold test rather
 * than a remainder test.
 */
export function isPlanetCycleComplete(subLevelsCurrent: number, tile: number): boolean {
  if (!tile || tile <= 0) {
    return false
  }
  return subLevelsCurrent + 1 > tile && subLevelsCurrent - tile >= 0
}

/**
 * Did turn number `turns` just complete another `tile`-long cycle?
 *
 * Used for cadence rather than for progress — how often a held planet's mines
 * still pay out, and how often it re-reports what it is waiting for.
 */
export function crossedPlanetCycle(turns: number, tile: number): boolean {
  if (!tile || tile <= 0) {
    return false
  }
  return Math.floor(turns / tile) > Math.floor((turns - 1) / tile)
}

/**
 * How far through its current cycle a planet is, 0..1.
 *
 * Clamped at 1: a planet holding for an upgrade it cannot pay for sits at a
 * full bar rather than overflowing past it.
 */
export function planetCycleProgress(subLevelsCurrent: number, tile: number): number {
  if (!tile || tile <= 0) {
    return 0
  }
  return Math.min(Math.max(subLevelsCurrent / tile, 0), 1)
}

/**
 * A planet that finished its cycle but could not pay for the next level is
 * holding: its progress stays put and the engine retries every turn.
 */
export function isPlanetHoldingForUpgrade(planet: { upgrade_hold_turns?: number, sub_levels_current: number, tile: number }): boolean {
  return !!planet.upgrade_hold_turns && isPlanetCycleComplete(planet.sub_levels_current, planet.tile)
}

/**
 * Will a GROWING planet's cycle land on the coming turn — i.e. should the card
 * warn "about to upgrade, stock the resources now"?
 *
 * `isPlanetCycleComplete` is the engine's own completion test, but the engine
 * runs it MID-tick, after it has already added the year's progress. Every state
 * a player ever sees is a post-tick broadcast, one increment behind, so from
 * the player's chair the engine attempts an upgrade next turn exactly when
 * `sub + 1 >= tile`. Testing the mid-tick rule against broadcast state is the
 * mistake that once killed this warning: a successful upgrade always leaves
 * `sub < tile` in the broadcast, so `sub >= tile` only ever showed up on a
 * planet already holding.
 *
 * A HOLDING planet also retries every turn, but that is its own state with its
 * own controls (`isPlanetHoldingForUpgrade`), so it is excluded here.
 */
export function willAttemptUpgradeNextYear(planet: { upgrade_hold_turns?: number, sub_levels_current: number, tile: number }): boolean {
  if (!planet?.tile || planet.tile <= 0) {
    return false
  }
  return !isPlanetHoldingForUpgrade(planet) && planet.sub_levels_current + 1 >= planet.tile
}

/**
 * Years until this planet's mines next pay out, or null when there is no
 * countdown to give (dice-driven token games, or a missing tile).
 *
 * Production and levelling share one clock while a planet is growing — the
 * mines pay on the same turn the level is attempted. They come apart the moment
 * the planet holds: `sub_levels_current` freezes at a full cycle while the
 * mines carry on paying every `tile` years, driven by `upgrade_hold_turns`.
 * That is why the on-screen fraction goes dead exactly when a player is staring
 * at it wondering what is happening, and why this returns a live number instead.
 */
export function yearsUntilNextYield(
  planet: { sub_levels_current: number, tile: number, upgrade_hold_turns?: number },
  disablePlanetTokens = true,
): number | null {
  if (!disablePlanetTokens || !planet?.tile || planet.tile <= 0) {
    return null
  }

  if (isPlanetHoldingForUpgrade(planet)) {
    // Holding: the engine tests the hold counter, not the progress bar. The
    // next payout is the next turn whose counter crosses a whole cycle.
    const held = planet.upgrade_hold_turns || 0
    for (let years = 1; years <= 500; years++) {
      if (crossedPlanetCycle(held + years - 1, planet.tile)) {
        return years
      }
    }
    return null
  }

  // Growing: progress gains one a turn and the mines pay when it reaches the
  // tile. Never less than one — the payout is always at least a turn away.
  return Math.max(1, Math.ceil(planet.tile - planet.sub_levels_current))
}
